import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import api from "../../../../../../services/api";
import SyncLoader from "react-spinners/SyncLoader";
import { useRoutes } from "../../../../../../hooks/routes";

import IFormCreateClause from "./dtos/IFormCreateClause";
import IListClauseCategoryApiProps from "./dtos/IListClauseCategoryApiProps";
import IHookForm from "../../../../../../shared/dtos/IHookForm";

import MasterAsideControls from "../../../../components/SidebarMaster/MasterAsideControls";
import HeaderMaster from "../../../../components/SidebarMaster/HeaderMaster";
import MasterContainer from "../../../../components/SidebarMaster/MasterContainer";
import MasterMain from "../../../../components/SidebarMaster/MasterMain";

import Button from "../../../../../../shared/components/Button";
import ButtonCancel from "../../../../../../shared/components/ButtonCancel";
import Input from "../../../../../../shared/components/Input";
import InputMultSelect from "../../../../../../shared/components/InputMultSelect";
import ChipInput from "../../../../../../shared/components/ChipInput";
import TooltipInformation from "../../../../../../shared/components/TooltipInformation";

import { Notify } from "../../../../../../shared/utils";

import { Main, Form, ContainerInput, LabelInput, ErrorInput } from "./styles";

const CreateClause: React.FC = () => {
  const [optionSelect, setOptionSelect] = useState<Array<string>>([]);
  const [loading, setLoading] = useState(false);

  const { getUrl } = useRoutes();

  const location = useLocation();
  const dataLocation: any = location.state;

  const navigate = useNavigate();

  useEffect(() => {
    getUrl("/create-clause");

    (async () => {
      const listClauseCategory = await api.get(
        "/bits-clause-categories/list-all-dashboard"
      );

      let options = [];

      listClauseCategory.data.forEach((item: IListClauseCategoryApiProps) => {
        options.push(`${item.title}||${item.id}`);
      });

      setOptionSelect(options);
    })();
  }, []);

  const formSchema = yup.object().shape({
    clause: yup.string().required("cláusula é um campo obrigatório"),
    key_words: yup
      .array()
      .of(yup.string())
      .required("palavras chave é um campo obrigatório"),
    categories: yup
      .array()
      .min(1, "categorias é um campo obrigatório")
      .required("categorias é um campo obrigatório"),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IHookForm>({
    mode: "onChange",
    resolver: yupResolver(formSchema),
  });

  const onSubmit: SubmitHandler<IFormCreateClause> = useCallback(
    async (formData) => {
      try {
        setLoading(true);

        let newCategories = [];

        formData.categories.forEach((item) => {
          const [label, id] = String(item).split("||");

          newCategories.push({
            bits_clause_categories_id: id,
          });
        });

        let keyWord = "";

        formData.key_words.forEach((item, index) => {
          if (index < formData.key_words.length - 1) {
            if (item.trim().length !== 0) {
              keyWord += `${item.toLowerCase().trim()},`;
            }
          } else {
            if (item.trim().length !== 0) {
              keyWord += `${item.toLowerCase().trim()}`;
            } else {
              keyWord = keyWord.substring(0, keyWord.length - 1);
            }
          }
        });

        const createClauseCategory = await api.post("/bits-clause", {
          clause: formData.clause,
          key_words: keyWord,
          categories: newCategories,
        });

        if (createClauseCategory.status === 200) {
          Notify({
            text: "Cláusula criada com sucesso",
            type: "success",
          });
          setLoading(false);
          navigate("/clauses", {
            state: { params: dataLocation?.params },
          });
        }
      } catch (err: any) {
        console.log(err?.response?.data);
        setLoading(false);
      }
    },
    [navigate, dataLocation]
  );

  return (
    <MasterContainer>
      <MasterAsideControls />
      <HeaderMaster namePage="Criar cláusula" />
      <MasterMain>
        <Main>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <ContainerInput>
              <LabelInput>Cláusula *</LabelInput>
              <Input
                label="clause"
                register={register}
                required={true}
                placeholder="Digite a cláusula"
              />
              {errors.clause && (
                <ErrorInput>{errors.clause.message}</ErrorInput>
              )}
            </ContainerInput>

            <ContainerInput>
              <LabelInput>Palavras chave *</LabelInput>

              <TooltipInformation
                title="A palavra chave é por onde o usuário irá encontrar essa 
                  cláusula. Então adicione o máximo de palavras que achar 
                  necessário. Aperte a tecla enter para adicionar a palavra chave"
                placement="top"
                maxWidth={300}
              />

              <ChipInput
                control={control}
                label="key_words"
                required={true}
                defaultValue={[]}
                placeholder="Palavras chave"
              />
              {errors.key_words && (
                <ErrorInput>{errors.key_words.message}</ErrorInput>
              )}
            </ContainerInput>

            <ContainerInput>
              <LabelInput>Selecione a categoria *</LabelInput>

              <TooltipInformation
                title="Se a categoria que você quer não estiver na lista. Você 
                  deve ir em categoria de cláusulas e cadastrar a categoria que 
                  deseja"
                placement="top"
                maxWidth={300}
              />

              <InputMultSelect
                control={control}
                label="categories"
                options={optionSelect}
              />
              {errors.categories && (
                <ErrorInput>{errors.categories.message}</ErrorInput>
              )}
            </ContainerInput>

            <ButtonCancel
              onClick={() => {
                navigate("/clauses", {
                  state: { params: dataLocation?.params },
                });
              }}
              type="button"
              style={{ marginTop: 30, marginRight: 10 }}
              disabled={loading}
            >
              Cancelar
            </ButtonCancel>

            {!loading && (
              <Button type="submit" style={{ marginTop: 30 }}>
                Cadastrar
              </Button>
            )}

            {loading && (
              <Button type="submit" style={{ marginTop: 30 }} disabled={true}>
                <SyncLoader
                  loading={loading}
                  color="#ffffff"
                  size={5}
                  style={{ marginLeft: 30, marginRight: 30 }}
                />
              </Button>
            )}
          </Form>
        </Main>
      </MasterMain>
    </MasterContainer>
  );
};

export default CreateClause;
